import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Botón con variantes de estilo y tamaño (estética shadcn simplificada). */
// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  // 21-oct, macOS 27: botón de contenido de 24 con radio 6 y letra 13 normal en la computadora
  // (chico 20, grande 28); en el teléfono todo mide 44 con radio 12 para el dedo.
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-normal ' +
    'rounded-lg lateral:rounded-md transition-colors focus-visible:outline-none ' +
    'focus-visible:ring-[3.5px] focus-visible:ring-acento/50 ' +
    'disabled:pointer-events-none disabled:opacity-45 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Preferido: lleno del acento.
        primary: 'bg-acento-lleno text-acento-sobre active:bg-[var(--acento-pulsado)]',
        // Por defecto: gris al 8 % (pulsado 16 %).
        secondary:
          'bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
        // Destructivo: tintado rojo con letra roja.
        danger: 'bg-[var(--mac-rojo-tintado)] text-[var(--mac-rojo-texto)] active:brightness-95',
        // Sin fondo (acción de barra o de fila).
        ghost:
          'text-slate-700 hover:bg-[var(--mac-btn)] active:bg-[var(--mac-btn-pulsado)] dark:text-slate-200',
        // Tintado: lo que antes iba con borde.
        outline:
          'bg-[var(--mac-tintado)] text-acento-texto active:bg-[var(--mac-tintado-pulsado)]',
      },
      size: {
        sm: 'h-11 px-3 text-sm lateral:h-5 lateral:px-2 lateral:text-xs [&_svg]:size-3.5',
        md: 'h-11 px-4 text-sm lateral:h-6 [&_svg]:size-3.5',
        lg: 'h-[50px] px-5 text-base lateral:h-7 lateral:text-sm [&_svg]:size-4',
        icon: 'h-11 w-11 lateral:h-7 lateral:w-7 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
